import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import moment from "moment-timezone";
import { MSpinner } from "components/Shared";
import MCaseView from "containers/Homeowner/MCaseView";
import { getCaseRCT, get360Value, getCaseInfo, getCaseSketch, setQATimeSpent, addReviewCaseTimeLog, getHasE2Value } from "actions/qualityAssurance";
import { Outline } from "components/QualityAssurance";
import FileManager from "containers/Homeowner/MCaseView/FileManager";
import {
  ACE_PDR_CONVERT_TO_APPRAISAL_ANY_STATUS,
  ACE_PDR_CONVERT_TO_APPRAISAL_COMPLETED,
  COVERAGE_CARD_VIEW,
  CLOCK_IN_OR_OUT_FUNCTION,
} from "typeConstants/rights";
import CaseIssues from "components/QualityAssurance/Sections/CaseDetails/CaseIssues/CaseIssues";
import { getCubiCasaSketch } from "requests/homeowner/caseforms";
import { userHasRight } from "selectors/rights";
import { openGoogleMapsLink } from "components/Shared/Functions";
import { getCaseForms } from "actions/homeowner/caseforms";
import { CaseTimeLogWorkTypeEnum } from "interfaces/Mueller.Business.Models.ts";
import Stopwatch from "components/Shared/StopWatch";
import DialogContainer from "react-md/lib/Dialogs";
import TextBlock from "components/Shared/TextBlock";
import { Button } from "react-md/lib/Buttons";

const SummaryAuditView = (props) => {
  const mounted = useRef(true);
  const currControlNumber = useRef(0);
  const currCasePrimaryStatus = useRef(); // Using a ref to capture this as a func gets called after control number changes
  const currCaseFlowStatus = useRef();
  const timeSpent = useRef(0);
  const [cubiCasa, setCubiCasa] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [sections, setSections] = useState([]);

  const {
    controlIdNumber,
    caseInfo,
    caseSketch,
    caseRCT,
    hasCaseRCT,
    getCaseRCT,
    value360,
    get360Value,
    getHasE2Value,
    hasE2Value,
    canViewCoverageCard,
    updatingCaseIssues,
    getCaseInfo,
    getCaseSketch,
    setQATimeSpent,
  } = props;

  useEffect(() => {
    loadData();
  return () => {
    if(currControlNumber.current) {
      addCaseTimeLog(currControlNumber.current);
      currControlNumber.current = 0;
      currCasePrimaryStatus.current = "";
      currCaseFlowStatus.current = "";
    }
    mounted.current = false;
  };
  }, []);

  const loadData = () => {
    currControlNumber.current = controlIdNumber;
    getSections();
    getCaseInfo(controlIdNumber);
    getCaseSketch({ ControlNumber: controlIdNumber });
    getCaseRCT({ ControlNumber: controlIdNumber });
    get360Value(controlIdNumber);
    getHasE2Value(controlIdNumber);
    getCubiCasaSketch(controlIdNumber, (data) => {
      setCubiCasa(data);
    }, () => {}, mounted);
    setInitialized(true);
  };

  useEffect(() => {
    getSections();
    if (caseInfo) {
      currCasePrimaryStatus.current = caseInfo.primaryStatus;
      currCaseFlowStatus.current = caseInfo.secondaryStatus;
    }
  }, [controlIdNumber, caseInfo, caseSketch, caseRCT, value360, hasE2Value]);

  useEffect(() => {
    if(controlIdNumber !== currControlNumber.current && currControlNumber.current) {
      addCaseTimeLog(currControlNumber.current);
      loadData();
    }
  }, [controlIdNumber]);

  useEffect(() => {
    timeSpent.current = props.timeSpent;
  }, [props.timeSpent]);

  const addCaseTimeLog = (controlNumber) => {
    const {
      addReviewCaseTimeLog,
      internetUserId,
    } = props;
    let timeLog = {
      controlIdNumber: controlNumber,
      internetUserId: internetUserId,
      workDate: moment().format("YYYY-MM-DD"),
      // if the case is completed and they are here, then they must be reviewing the feedback..else they are auditing
      workTypeId: currCasePrimaryStatus.current === 'COMPLETED' || currCaseFlowStatus.current === 'COMPLETED' ? CaseTimeLogWorkTypeEnum.FS_REVIEW_AUDIT_FEEDBACK : CaseTimeLogWorkTypeEnum.FS_REVIEW_AUDIT,
      timeMinutes: Number((timeSpent.current / 60)),
    };
    addReviewCaseTimeLog(timeLog);
    setQATimeSpent(0.0, () => {});
  };

  const getSections = () => {
    let _sections = [];
    _sections.push({
      title: `Case Audit`,
      id: "QACaseAudit",
      formStatus: "clean",
      external: true,
    });
    _sections.push({
      title: `${controlIdNumber} Outline`,
      id: "Header",
      formStatus: "clean",
      external: true,
      component: (<Outline
                    controlNumber={controlIdNumber}
                    canViewCoverageCard={canViewCoverageCard}
                    displayAddressMap={openGoogleMapsLink}
                  />)
    });
    _sections.push({
      title: `Photos`,
      id: "Photos",
      formStatus: "clean",
      external: true,
    });
    if (hasCaseRCT) {
      _sections.push({
        title: "RCT",
        id: "Calc",
        formStatus: "clean",
        external: true,
      });
    }
    if (value360.hasValue) {
      _sections.push({
        title: "360 Value",
        id: "360Value",
        formStatus: "clean",
        external: true,
      });
    }
    if (hasE2Value) {
      _sections.push({
        title: "e2Value",
        id: "e2Value",
        formStatus: "clean",
        external: true,
      });
    }
    if (caseSketch) {
      _sections.push({
        title: `Sketch`,
        id: "Sketch",
        formStatus: "clean",
        external: true,
      });
    }
    if (cubiCasa?.sketchComp) {
      _sections.push({
        title: `CubiCasa`,
        id: "CubiCasa",
        formStatus: "clean",
        external: true,
      });
    }
    _sections.push({
      title: `Case Details`,
      id: "QACaseDetails",
      formStatus: "clean",
      external: true,
      component: (<div />)
    });
    _sections.push({
      title: `Case Issues`,
      id: "CaseIssues",
      formStatus: "clean",
      external: true,
      component: (<CaseIssues controlNumber={controlIdNumber} updatingCaseIssues={updatingCaseIssues} />)
    });
    _sections.push({
      title: "File Manager",
      id:  "FileManager",
      formStatus: "clean",
      external: true,
      component: (
        <FileManager
          controlNumber={controlIdNumber}
          canRegeneratePdf={false}
        />
        )
    });
    if (props.rights.includes(ACE_PDR_CONVERT_TO_APPRAISAL_ANY_STATUS)
        || props.rights.includes(ACE_PDR_CONVERT_TO_APPRAISAL_COMPLETED)
    ) {
      _sections.push({
        title: "Case Actions",
        id: "Validation",
        formStatus: "clean"
      });
    }
    setSections(_sections);
  };

  const clockInActions = [
    <Button
      className="m-t-10"
      key="continue"
      raised
      primary
      onClick={() => props.history.push("/qa-dashboard")}
    >
      QA Dashboard
    </Button>
  ];

  if(props.rights.includes(CLOCK_IN_OR_OUT_FUNCTION) && !(props.activeClock && props.activeClock.clockInDate)) {
    return (
      <DialogContainer
        id="dialog-clock-in"
        visible={props.rights.includes(CLOCK_IN_OR_OUT_FUNCTION) && !(props.activeClock && props.activeClock.clockInDate)}
        title="Please Clock In"
        modal
        width={500}
        focusOnMount={false}
        actions={clockInActions}
      >
        <TextBlock field={`
          You are not currently clocked in. Case production cannot begin until you are clocked in.
          Please either navigate to the QA Dashboard page to clock in or see your manager for a clock-in
          correction before beginning QA.`} />
      </DialogContainer>
    );
  }
  if (!initialized || !caseInfo) {
    return (<MSpinner />);
  }
  return (
    <>
      {props.rights.includes(CLOCK_IN_OR_OUT_FUNCTION) &&
          <div style={{ margin: "auto", marginTop: 0 }}>
          <div className="timer-label">
            <Stopwatch
              key={initialized}
              hideStopStart
              tickOnMount={initialized}
              onChange={secondsElapsed => {
                setQATimeSpent(secondsElapsed);
              }}
              paused={false}
              label={"Time on Case: "}
              className="stopwatch-leftAlign"
            />
          </div>
        </div>}
      <MCaseView
        externalFormSet={sections.length ? sections : getSections()}
        forcePushToPane={["QACaseAudit", "Photos"]}
        controlNumberOverride={controlIdNumber}
        firstChildInitialSize={"50%"}
      />
    </>
  );
};

SummaryAuditView.propTypes = {
  controlIdNumber: PropTypes.number.isRequired,
  qaCase: PropTypes.object,
  caseInfo: PropTypes.object,
  caseSketch: PropTypes.object,
  caseRCT: PropTypes.object,
  hasCaseRCT: PropTypes.bool,
  getCaseRCT: PropTypes.func,  
  value360: PropTypes.object,
  get360Value: PropTypes.func,
  getHasE2Value: PropTypes.func,
  hasE2Value: PropTypes.bool,
  rights: PropTypes.array,
  canViewCoverageCard: PropTypes.bool,
  updatingCaseIssues: PropTypes.bool,
  getCaseForms: PropTypes.func,
  getCaseInfo: PropTypes.func,
  getCaseSketch: PropTypes.func,
  setQATimeSpent: PropTypes.func,
  addReviewCaseTimeLog: PropTypes.func,
  internetUserId: PropTypes.number,
  timeSpent: PropTypes.object,
  activeClock: PropTypes.object,
};

const mapStateToProps = state => {
  return {
    value360: state.qualityAssurance.value360,
    hasE2Value: state.qualityAssurance.hasE2Value,
    qaCase: state.qualityAssurance.qaCase,
    caseInfo: state.qualityAssurance.qaCase.caseInfo,
    caseSketch: state.qualityAssurance.qaCase.caseSketch,
    caseRCT: state.qualityAssurance.caseRCT,
    hasCaseRCT: state.qualityAssurance.hasCaseRCT,
    rights: state.user.profile.rights,
    canViewCoverageCard: userHasRight(COVERAGE_CARD_VIEW)(state),
    updatingCaseIssues: state.qualityAssurance.updatingCaseIssues,
    internetUserId: state.user.profile.internetUserId,
    timeSpent: state.qualityAssurance.qaVariables.timeSpent,
    activeClock: state.user.activeClock,
  };
};

const mapDispatchToProps = dispatch => ({
  getCaseRCT(payload, callback) {
    dispatch(getCaseRCT(payload, callback));
  },
  get360Value(controlNumber) {
    dispatch(get360Value(controlNumber));
  },
  getHasE2Value(controlNumber) {
    dispatch(getHasE2Value(controlNumber));
  },
  getCaseForms(controlNumber) {
    dispatch(getCaseForms(controlNumber));
  },
  getCaseInfo(payload, callback) {
    dispatch(getCaseInfo(payload, callback));
  },
  getCaseSketch(payload, callback) {
    dispatch(getCaseSketch(payload, callback));
  },
  setQATimeSpent(qaTimeSpent) {
    dispatch(setQATimeSpent(qaTimeSpent));
  },
  addReviewCaseTimeLog(dto) {
    dispatch(addReviewCaseTimeLog(dto, false));
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SummaryAuditView));
